import {
  createProgramFn,
  deleteProgramFn,
  listAllProgramsOptions,
  toggleProgramActiveFn,
  toggleProgramPublicFn,
  updateProgramFn,
} from "@/actions/program/handler";
import type { SelectTrainingProgram } from "@/api/db/schema/training-programs";
import { ProgramForm } from "@/components/admin/ProgramForm";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  redirect,
  useRouteContext,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Edit,
  Eye,
  EyeOff,
  MoreVertical,
  Plus,
  Power,
  PowerOff,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/admin/programs")({
  beforeLoad: async ({ context }) => {
    if (!context.user || context.user.role !== "admin") {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context }) =>
    await context.queryClient.ensureQueryData(listAllProgramsOptions()),
  component: RouteComponent,
});

function RouteComponent() {
  const { data } = useSuspenseQuery(listAllProgramsOptions());
  const { queryClient } = useRouteContext({ from: "/_app/admin/programs" });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProgram, setEditingProgram] =
    useState<SelectTrainingProgram | null>(null);
  const [deleteProgram, setDeleteProgram] =
    useState<SelectTrainingProgram | null>(null);

  const programs = data.body?.programs ?? [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: createProgramFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listAllProgramsOptions().queryKey,
      });
      toast.success("Program başarıyla oluşturuldu");
      setIsFormOpen(false);
      setEditingProgram(null);
    },
    onError: (error) => {
      toast.error("Program oluşturulamadı", {
        description: error.message,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateProgramFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listAllProgramsOptions().queryKey,
      });
      toast.success("Program başarıyla güncellendi");
      setIsFormOpen(false);
      setEditingProgram(null);
    },
    onError: (error) => {
      toast.error("Program güncellenemedi", {
        description: error.message,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteProgramFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listAllProgramsOptions().queryKey,
      });
      toast.success("Program başarıyla silindi");
      setDeleteProgram(null);
    },
    onError: (error) => {
      toast.error("Program silinemedi", {
        description: error.message,
      });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: toggleProgramActiveFn,
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: listAllProgramsOptions().queryKey,
      });
      toast.success(response.body?.message || "Durum değiştirildi");
    },
    onError: (error) => {
      toast.error("Durum değiştirilemedi", {
        description: error.message,
      });
    },
  });

  const togglePublicMutation = useMutation({
    mutationFn: toggleProgramPublicFn,
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: listAllProgramsOptions().queryKey,
      });
      toast.success(response.body?.message || "Görünürlük değiştirildi");
    },
    onError: (error) => {
      toast.error("Görünürlük değiştirilemedi", {
        description: error.message,
      });
    },
  });

  // Handlers
  const handleCreate = () => {
    setEditingProgram(null);
    setIsFormOpen(true);
  };

  const handleEdit = (program: SelectTrainingProgram) => {
    setEditingProgram(program);
    setIsFormOpen(true);
  };

  const handleDelete = (program: SelectTrainingProgram) => {
    setDeleteProgram(program);
  };

  const handleToggleActive = (program: SelectTrainingProgram) => {
    toggleActiveMutation.mutate({ data: { programId: program.programId } });
  };

  const handleTogglePublic = (program: SelectTrainingProgram) => {
    togglePublicMutation.mutate({ data: { programId: program.programId } });
  };

  const handleFormSubmit = async (formData: {
    name: string;
    description?: string;
    durationWeeks: number;
    daysPerWeek: number;
    difficulty: number;
    isPublic: boolean;
    displayOrder: number;
  }) => {
    if (editingProgram) {
      await updateMutation.mutateAsync({
        data: {
          programId: editingProgram.programId,
          ...formData,
        },
      });
    } else {
      await createMutation.mutateAsync({ data: formData });
    }
  };

  const handleConfirmDelete = () => {
    if (deleteProgram) {
      deleteMutation.mutate({ data: { programId: deleteProgram.programId } });
    }
  };

  const difficultyLabels = ["Başlangıç", "Kolay", "Orta", "Zor", "İleri"];
  const difficultyColors = [
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
    "bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-400",
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
    "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
    "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  ];

  return (
    <div className="container mx-auto max-w-7xl space-y-8 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      {/* Breadcrumb */}
      <div className="mb-6">
        <Button asChild variant="ghost" className="mb-4">
          <Link to="/admin">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Admin Paneli
          </Link>
        </Button>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-bold text-2xl sm:text-3xl">Program Yönetimi</h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            Antrenman programlarını oluşturun ve yönetin
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          <span className="hidden sm:inline">Yeni Program</span>
          <span className="sm:hidden">Ekle</span>
        </Button>
      </div>

      {/* Program List */}
      {programs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-2 font-semibold text-lg">Henüz Program Yok</h3>
            <p className="mb-4 text-center text-muted-foreground">
              İlk antrenman programınızı oluşturun
            </p>
            <Button onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Program Oluştur
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {programs.map((program) => (
            <Card
              key={program.programId}
              className={`transition-opacity ${!program.isActive ? "opacity-60" : ""}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{program.name}</CardTitle>
                    {program.description && (
                      <p className="mt-1 line-clamp-2 text-muted-foreground text-sm">
                        {program.description}
                      </p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(program)}>
                        <Edit className="mr-2 h-4 w-4" />
                        Düzenle
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleToggleActive(program)}
                      >
                        {program.isActive ? (
                          <>
                            <PowerOff className="mr-2 h-4 w-4" />
                            Devre Dışı Bırak
                          </>
                        ) : (
                          <>
                            <Power className="mr-2 h-4 w-4" />
                            Aktifleştir
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleTogglePublic(program)}
                      >
                        {program.isPublic ? (
                          <>
                            <EyeOff className="mr-2 h-4 w-4" />
                            Özel Yap
                          </>
                        ) : (
                          <>
                            <Eye className="mr-2 h-4 w-4" />
                            Herkese Aç
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleDelete(program)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Sil
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                {/* Badges */}
                <div className="mb-3 flex flex-wrap gap-2">
                  <Badge
                    variant="secondary"
                    className={difficultyColors[program.difficulty - 1]}
                  >
                    {difficultyLabels[program.difficulty - 1]}
                  </Badge>
                  {!program.isActive && (
                    <Badge variant="outline" className="text-muted-foreground">
                      Pasif
                    </Badge>
                  )}
                  {program.isPublic && (
                    <Badge variant="outline" className="text-green-600">
                      <Eye className="mr-1 h-3 w-3" />
                      Public
                    </Badge>
                  )}
                </div>

                {/* Stats */}
                <div className="flex items-center gap-4 text-muted-foreground text-sm">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span>{program.durationWeeks} hafta</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>{program.daysPerWeek} gün/hafta</span>
                  </div>
                </div>

                {/* View Program Link */}
                <Button asChild variant="outline" className="mt-4 w-full">
                  <Link
                    to="/admin/program/$programId"
                    params={{ programId: program.programId }}
                  >
                    İçeriği Düzenle
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProgram ? "Programı Düzenle" : "Yeni Program Oluştur"}
            </DialogTitle>
          </DialogHeader>
          <ProgramForm
            initialData={editingProgram || undefined}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setIsFormOpen(false);
              setEditingProgram(null);
            }}
            isLoading={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deleteProgram}
        onOpenChange={(open) => !open && setDeleteProgram(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Programı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteProgram?.name}" programını silmek istediğinize emin
              misiniz? Bu işlem geri alınamaz ve tüm program günleri de
              silinecektir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              İptal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
